import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';
import { AppRoutes } from 'src/app/app-routes';
import { ConsentTaxFolderUI } from 'src/app/interfaces/consent-tax-folder.interface';
import { CustomerDataUI } from 'src/app/interfaces/customer-data.interface';
import { EncryptionTarget } from 'src/app/interfaces/tax-folder.interface';
import { EncryptionService } from 'src/app/services/encryption/encryption.service';
import { SnackbarService } from 'src/app/services/snackbars/snackbar.service';
import { StoreService } from 'src/app/services/store/store.service';
import { SnackbarMessageKey } from 'src/app/utils/enums';
import { mapper } from 'src/app/utils/mapper';
import { buildRequestGenerateOtp } from 'src/app/utils/utils';

@Component({
	selector: 'app-tax-folder',
	templateUrl: './tax-folder.component.html',
	styleUrls: ['./tax-folder.component.scss'],
})
export class TaxFolderComponent implements OnInit {
	readonly vm$ = combineLatest({
		consent: this.storeService.consent$,
		customerData: this.storeService.customerData$,
	}).pipe(
		filter(
			(vm): vm is { consent: ConsentTaxFolderUI; customerData: CustomerDataUI } =>
				!!vm.consent && !!vm.customerData
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	tooltips: Record<string, boolean> = {
		password: false,
	};

	taxFolderForm: FormGroup;

	constructor(
		private readonly snackbarService: SnackbarService,
		private readonly storeService: StoreService,
		private readonly destroyRef: DestroyRef,
		private readonly fb: FormBuilder,
		private readonly router: Router,
		private readonly encryptionService: EncryptionService
	) {}

	get passwordControl() {
		return this.taxFolderForm.controls.password;
	}

	ngOnInit(): void {
		this.initForm();
	}

	initForm(): void {
		this.taxFolderForm = this.fb.nonNullable.group({
			password: ['', [Validators.required, Validators.minLength(4)]],
			termsAndConditions: [false, Validators.requiredTrue],
		});

		this.setupErrorTrigger(this.passwordControl);
	}

	private setupErrorTrigger(control: AbstractControl | null, delay = 600): void {
		control?.statusChanges
			.pipe(
				debounceTime(delay),
				distinctUntilChanged(),
				filter(status => status === 'INVALID'),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				control.markAsTouched({ onlySelf: true });
			});
	}

	onContinue(customerData: CustomerDataUI, consent: ConsentTaxFolderUI): void {
		if (this.taxFolderForm.invalid) {
			return;
		}

		const encrypted: EncryptionTarget = {
			id: customerData.userRut.replace(/\./g, '').trim(),
			password: this.passwordControl.value,
			exp: consent.expiration,
		};

		this.storeService
			.taxFolder(
				mapper.requestTaxFolder(
					this.encryptionService.encrypt(JSON.stringify(encrypted)),
					customerData.companyRut,
					consent.id
				)
			)
			.pipe(
				tap(({ validPassword }) => {
					if (!validPassword) {
						this.snackbarService.show(SnackbarMessageKey.CTE_PASSWORD_ERROR);
						this.passwordControl.setErrors({
							...this.passwordControl.errors,
							invalidPassword: true,
						});
						this.passwordControl.markAsTouched();
					}
				}),
				filter(resp => resp.validPassword),
				switchMap(() =>
					this.storeService.generateSendOtp(buildRequestGenerateOtp(customerData.companyRut))
				),
				tap(() => this.router.navigate([AppRoutes.OTP]))
			)
			.subscribe();
	}
}
